// k:env-boundary — resolves ${VAR} references against the environment (BE-005).
/**
 * Resolve a `${VAR_NAME}` reference against `process.env`.
 *
 * If `value` does not start with `${`, it is returned unchanged so
 * literal API keys (still discouraged) continue to work. If the
 * pattern matches, the env var is read; an unset var is a fatal
 * config error rather than silently empty. A Sifter that dispatches
 * to Anthropic with no API key would otherwise loop on 401s with
 * spend tracker burning through the cap-exceeded refusal path.
 *
 * Whitespace inside `${...}` is intentionally rejected: it almost
 * always indicates a typo rather than an env-var name. Match POSIX
 * env-var conventions: `[A-Za-z_][A-Za-z0-9_]*`.
 */
const ENV_REF_PATTERN = /^\$\{([A-Za-z_][A-Za-z0-9_]*)\}$/;

export function resolveEnvRef(value: string, fieldName?: string): string {
  const match = ENV_REF_PATTERN.exec(value);
  if (!match) return value;
  const varName = match[1];
  const resolved = process.env[varName];
  if (resolved === undefined || resolved === '') {
    const subject = fieldName ? `${fieldName} (${value})` : value;
    throw new Error(
      `Environment variable "${varName}" referenced by ${subject} is not set. ` +
        `Export it before starting the Sifter, or hard-code the value in the YAML.`,
    );
  }
  return resolved;
}

/** True iff `value` is an `${ENV_VAR}` reference (regardless of whether the var is set). */
export function isEnvRef(value: string): boolean {
  return ENV_REF_PATTERN.test(value);
}

/**
 * Resolve a required string config value from a YAML field (typically a
 * `${ENV_VAR}` reference) with a named env-var fallback. Used by the
 * dynamic-key executors to read their Keep service credentials, which the
 * hosted charts supply via env rather than YAML.
 */
export function resolveConfigStringOrEnv(
  config: Record<string, unknown>,
  field: string,
  envFallback: string,
): string {
  const raw = config[field];
  if (typeof raw === 'string' && raw.length > 0) {
    return resolveEnvRef(raw, field);
  }
  const fromEnv = process.env[envFallback];
  if (fromEnv && fromEnv.length > 0) {
    return fromEnv;
  }
  throw new Error(
    `dynamic-key mode requires "${field}" in config or the ${envFallback} env var`,
  );
}
